import React from "react";
import type { CSSProperties } from "react";

import type { SortingState } from "@tanstack/react-table-v9";
import styled from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import TableLink from "../../Table/TableLink";
import type { TableV2ColumnDef } from "../columns";
import { defaultConfig, joinKey } from "./engine";
import { FORMAT_PRESETS, formatRuleStyle } from "./formatting";
import type { ValueStats } from "./formatting";
import type {
  PivotColumnKey,
  PivotField,
  PivotRowNode,
  PivotSort,
  PivotValue,
} from "./types";
import TableV2IconButton from "../components/TableV2IconButton";
import { formatCellValue, isNegativeValue } from "../formatters";
import type { TableV2MenuItem } from "../types";
import { TOTAL_KEY } from "./types";
import type { PivotController } from "./usePivotController";
import { fieldLabel, valueLabel, valueMeta } from "./valueUtils";

export const PIVOT_LABEL_COLUMN_ID = "__pivotLabel";

export interface PivotCellClick {
  row: PivotRowNode;
  column: PivotColumnKey;
  value: PivotValue;
}

const HeaderLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-width: 0;
`;

const Spacer = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 24px;
`;

const Label = styled.span`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-width: 0;
`;

const Muted = styled.span`
  font-weight: normal;
  color: ${colorTheme("neutralL2")};
`;

const RowError = styled.span`
  display: flex;
  align-items: center;
  font-weight: normal;
  color: ${colorTheme("danger")};
`;

interface ColumnTarget {
  columnKey: string;
  valueId: string;
}

const valueColumnId = (columnKey: string, valueId: string) =>
  `pv:${columnKey}:${valueId}`;

export const pivotSortingState = (
  sort: PivotSort | undefined,
): SortingState => {
  if (!sort) return [];
  if (sort.by === "label")
    return [{ id: PIVOT_LABEL_COLUMN_ID, desc: !!sort.desc }];
  return [
    {
      id: valueColumnId(sort.columnKey ?? TOTAL_KEY, sort.valueId),
      desc: !!sort.desc,
    },
  ];
};

export const toPivotSort = (
  sorting: SortingState,
  targets: Map<string, ColumnTarget>,
): PivotSort | undefined => {
  const first = sorting[0];
  if (!first) return undefined;
  if (first.id === PIVOT_LABEL_COLUMN_ID)
    return { by: "label", desc: first.desc };
  const target = targets.get(first.id);
  return target
    ? {
        by: "value",
        valueId: target.valueId,
        columnKey: target.columnKey,
        desc: first.desc,
      }
    : undefined;
};

const leafStats = (
  rows: PivotRowNode[],
  leafDepth: number,
  columns: PivotColumnKey[],
  value: PivotValue,
  columnDepth: number,
) => {
  let min = Infinity;
  let max = -Infinity;
  const visit = (nodes: PivotRowNode[]) =>
    nodes.forEach((row) => {
      if (row.depth === leafDepth) {
        columns.forEach((column) => {
          if (column.kind !== "leaf" && columnDepth > 0) return;
          const n = row.cells[column.key]?.[value.id];
          if (n === null || n === undefined) return;
          if (n < min) min = n;
          if (n > max) max = n;
        });
      }
      if (row.children) visit(row.children);
    });
  visit(rows);
  return min <= max ? ({ min, max } as ValueStats) : undefined;
};

export const buildPivotColumns = <TData,>(
  controller: PivotController<TData>,
  fields: PivotField<TData>[],
  live: () => PivotController<TData>,
  onCellClick?: (cell: PivotCellClick) => void,
): {
  columns: TableV2ColumnDef<PivotRowNode>[];
  targets: Map<string, ColumnTarget>;
} => {
  const config = defaultConfig(controller.config);
  const { result } = controller;
  const values = config.values;
  const columnDepth = config.columns.length;
  const leafDepth = config.rows.length - 1;
  const targets = new Map<string, ColumnTarget>();
  const stats = new Map(
    values.map((value) => [
      value.id,
      value.format?.length
        ? leafStats(
            result.rows,
            leafDepth,
            result.columnKeys,
            value,
            columnDepth,
          )
        : undefined,
    ]),
  );

  const setFormat = (value: PivotValue, format: PivotValue["format"]) =>
    live().setConfig((current) => {
      return {
        ...current,
        values: current.values.map((v) =>
          v.id === value.id ? { ...v, format } : v,
        ),
      };
    });

  const formatMenu = (value: PivotValue): TableV2MenuItem[] => [
    ...FORMAT_PRESETS.map((preset) => {
      return {
        key: `format-${preset.key}`,
        label: preset.label,
        icon: "fa-palette",
        run: () => setFormat(value, preset.rules),
      };
    }),
    ...(value.format?.length
      ? [
          {
            key: "format-clear",
            label: "Clear formatting",
            icon: "fa-eraser",
            run: () => setFormat(value, undefined),
          },
        ]
      : []),
  ];

  const valueColumn = (
    column: PivotColumnKey,
    value: PivotValue,
    header: string,
    separator = false,
  ): TableV2ColumnDef<PivotRowNode> => {
    const id = valueColumnId(column.key, value.id);
    targets.set(id, { columnKey: column.key, valueId: value.id });
    const meta = valueMeta(fields, value);
    const isTotal = column.kind !== "leaf" && columnDepth > 0;
    const formattable = !isTotal;
    const read = (row: PivotRowNode) =>
      row.hasChildren && !config.rowSubtotals
        ? null
        : (row.cells[column.key]?.[value.id] ?? null);

    return {
      id,
      header,
      size: 160,
      minSize: 80,
      accessorFn: read,
      meta: {
        ...meta,
        label:
          column.kind === "total"
            ? `Grand total ${valueLabel(fields, value)}`
            : header,
        separator,
        menuItems: formatMenu(value),
        getCellStyle: (n, row) => {
          const style: CSSProperties = {};
          if (column.kind === "total" && columnDepth > 0)
            style.fontWeight = 600;
          const node = row as PivotRowNode;
          if (formattable && node.depth === leafDepth) {
            Object.assign(
              style,
              formatRuleStyle(
                value.format,
                n as number | null,
                stats.get(value.id),
              ).style,
            );
          }
          if (meta.negative?.red && isNegativeValue(n))
            style.color = colorTheme("danger");
          return style;
        },
      },
      cell: ({ row, getValue }) => {
        const n = getValue() as number | null;
        const text = formatCellValue(n, meta) ?? "";
        const icon =
          formattable && row.original.depth === leafDepth
            ? formatRuleStyle(value.format, n, stats.get(value.id)).icon
            : undefined;
        const content = (
          <>
            {icon && (
              <i
                className={
                  icon === "up"
                    ? "fa-solid fa-caret-up"
                    : "fa-solid fa-caret-down"
                }
                style={{ marginRight: gridSpacing[1] }}
              />
            )}
            {text}
          </>
        );
        return onCellClick && text ? (
          <TableLink
            style={{ color: "inherit" }}
            onClick={() => onCellClick({ row: row.original, column, value })}
          >
            {content}
          </TableLink>
        ) : (
          content
        );
      },
      footer: config.rowGrandTotal
        ? () =>
            formatCellValue(
              result.grandTotal[column.key]?.[value.id] ?? null,
              meta,
            ) ?? ""
        : undefined,
    };
  };

  const leafFor = (
    column: PivotColumnKey,
    header: string,
    separator: boolean,
  ): TableV2ColumnDef<PivotRowNode> =>
    values.length > 1 || columnDepth === 0
      ? {
          id: `pg:${column.kind}:${column.key}`,
          header,
          meta: { separator },
          columns: values.map((value, i) =>
            valueColumn(
              column,
              value,
              valueLabel(fields, value),
              separator && i === 0,
            ),
          ),
        }
      : valueColumn(column, values[0], header, separator);

  const root: TableV2ColumnDef<PivotRowNode>[] = [];
  const groups = new Map<string, TableV2ColumnDef<PivotRowNode>[]>();
  const childrenOf = (
    path: string[],
    labels: string[],
  ): TableV2ColumnDef<PivotRowNode>[] => {
    let parent = root;
    path.forEach((_, i) => {
      const key = joinKey(path.slice(0, i + 1));
      let children = groups.get(key);
      if (!children) {
        children = [];
        groups.set(key, children);
        parent.push({
          id: `pg:group:${key}`,
          header: labels[i],
          meta: { separator: root.length > 0 && i === 0 },
          columns: children,
        });
      }
      parent = children;
    });
    return parent;
  };

  if (values.length) {
    let previousTop: string | undefined;
    result.columnKeys.forEach((column, index) => {
      if (column.kind === "total") {
        if (columnDepth === 0) {
          values.forEach((value) =>
            root.push(valueColumn(column, value, valueLabel(fields, value))),
          );
        } else {
          root.push(leafFor(column, "Grand total", true));
        }
        return;
      }
      const depth = column.path.length;
      const startsGroup =
        (columnDepth > 1 || values.length > 1) &&
        index > 0 &&
        column.path[0] !== previousTop;
      previousTop = column.path[0];
      const parent = childrenOf(column.path.slice(0, depth - 1), column.labels);
      const label = column.labels[depth - 1];
      parent.push(
        leafFor(
          column,
          column.kind === "subtotal" ? `${label} total` : label,
          startsGroup || column.kind === "subtotal",
        ),
      );
    });
  }

  const labelColumn: TableV2ColumnDef<PivotRowNode> = {
    id: PIVOT_LABEL_COLUMN_ID,
    header: () => {
      const title = config.rows
        .map((dim) => fieldLabel(fields, dim.field))
        .join(" › ");
      if (config.rows.length < 2) return title;
      const anyOpen = live().hasExpanded();
      return (
        <HeaderLabel>
          <TableV2IconButton
            icon={anyOpen ? "fa-solid fa-angles-up" : "fa-solid fa-angles-down"}
            label={anyOpen ? "Collapse all" : "Expand all"}
            onClick={(event) => {
              event.stopPropagation();
              if (anyOpen) live().collapseAll();
              else live().expandAll();
            }}
          />
          {title}
        </HeaderLabel>
      );
    },
    size: 240,
    minSize: 120,
    enableHiding: false,
    enableSorting: config.rows.length > 0,
    accessorFn: (row) => row.label,
    meta: { label: "Rows", lockOrder: true },
    cell: ({ row }) => {
      const node = row.original;
      const open = live().isExpanded(node.key);
      return (
        <Label style={{ paddingLeft: row.depth * gridSpacing[5] }}>
          {node.hasChildren ? (
            <TableV2IconButton
              tooltip={false}
              icon={open ? "fa-regular fa-minus" : "fa-regular fa-plus"}
              label={`${open ? "Collapse" : "Expand"} ${node.label}`}
              onClick={(event) => {
                event.stopPropagation();
                live().toggleExpanded(node);
              }}
            />
          ) : (
            config.rows.length > 1 && <Spacer />
          )}
          {node.label}
          {node.hasChildren && node.children && (
            <Muted>({node.children.length})</Muted>
          )}
          {live().rowError(node.key) && (
            <RowError>
              {live().rowError(node.key)}
              <TableV2IconButton
                icon="fa-regular fa-rotate-right"
                label={`Retry ${node.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  live().retryRow(node);
                }}
              />
            </RowError>
          )}
        </Label>
      );
    },
    footer: config.rowGrandTotal ? "Grand total" : undefined,
  };

  return { columns: [labelColumn, ...root], targets };
};
